import { useEffect, useRef, useState, ChangeEvent } from "react";
import useProducts from "../../../../hooks/useProducts";
import useCategory from "../../../../hooks/useCategory";
import { useSearch } from "../../../../components/SearchContext";
import { useUser } from "src/shared/context/UserContext";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import usePermission from "src/business-types/grocery/hooks/usePermission";
import { Product } from "src/shared/services/interfacev2";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { exportProduct, importProduct } from "src/shared/api";

export function useProductsPage(usedPopup: boolean) {
  const [addProduct, setAddProduct] = useState(false);
  const [editProduct, setEditProduct] = useState<Product>();
  const [confirmDeleteList, setConfirmDeleteList] = useState<string[]>();
  const [deleteIds, setDeleteIds] = useState<string[]>();
  const [deleted, setDeleted] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDropdown, setShowDropdown] = useState(false);
  const [orderby, setOrderBy] = useState("");
  const [field, setField] = useState("");

  const { userConfigration } = useUser();
  const { searchTerm, setSearchTerm } = useSearch();
  const { category } = useCategory();
  const { hasPermission } = usePermission();
  const amountFormat = useFormatAmount();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { product, loadingPro, productPage, DeletedProduct, reloadProduct, categoryidBased, searchProduct } = useProducts(
    deleted,
    page + 1,
    rowsPerPage,
    field,
    orderby,
  );

  const checkPermission = (key: string) => hasPermission(key);
  const createProduct = checkPermission("product_create");
  const updateProduct = checkPermission("product_update");
  const deletedProduct = checkPermission("product_deleted");

  useEffect(() => {
    categoryidBased(filterCategory || "");
  }, [filterCategory]);

  useEffect(() => {
    searchProduct(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setDeleteIds(undefined);
    setConfirmDeleteList(undefined);
  }, [product]);

  useEffect(() => {
    setRowsPerPage(userConfigration?.tableDefaultSize || 10);
    if (!usedPopup) setSearchTerm("");
  }, [userConfigration, usedPopup]);

  const handleClose = () => {
    setAddProduct(false);
    setEditProduct(undefined);
    setConfirmDeleteList(undefined);
    setDeleteIds(undefined);
    reloadProduct();
  };

  const handleDelete = (selectedIds: (string | number)[]) => {
    const ids = selectedIds.map(String);
    setDeleteIds(ids);
    const names = product.filter((p) => ids.includes(p._id.$oid)).map((p) => p.productName);
    setConfirmDeleteList(names);
  };

  const confirmDelete = async () => {
    if (!deleteIds) return;
    DeletedProduct(deleteIds);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await exportProduct();
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const filename = res.headers["content-disposition"]?.match(/filename="?(.+)"?/)?.[1] || "product_export.xlsx";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch {
      notifyError("Failed to export products");
    } finally {
      setLoading(false);
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await importProduct(formData);
      reloadProduct();
      notifySuccess("Products imported successfully");
    } catch {
      notifyError("Import failed");
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return {
    // state
    addProduct,
    setAddProduct,
    editProduct,
    setEditProduct,
    confirmDeleteList,
    deleted,
    setDeleted,
    isLoading,
    setLoading,
    filterCategory,
    setFilterCategory,
    page,
    rowsPerPage,
    showDropdown,
    setShowDropdown,
    orderby,
    field,
    // derived
    product,
    loadingPro,
    productPage,
    category,
    searchTerm,
    setSearchTerm,
    createProduct,
    updateProduct,
    deletedProduct,
    amountFormat,
    fileInputRef,
    // handlers
    handleClose,
    handleDelete,
    confirmDelete,
    handleExport,
    handleImportClick,
    handleImportFile,
    handlePageChange,
    handleRowsChange,
    setField,
    setOrderBy,
  };
}
